// Random anonymous realm/village names. Shared so the server can seed a new
// faction and the client can offer a "reroll" in the name-your-village prompt —
// the point is that a player's REAL sign-in name is never used as their village
// name. Pastoral prefix + suffix, e.g. "Ashford", "Frostmere", "Willowdale".

import { pick, randInt } from './rng'

const REALM_A = [
  'Oaken', 'Ash', 'Elm', 'Birch', 'Mill', 'Bram', 'Fen', 'Green', 'Stone', 'River',
  'Wheat', 'Apple', 'Hollow', 'Mead', 'Barley', 'Clover', 'Hazel', 'Willow', 'Rowan', 'Honey',
  'Grey', 'Frost', 'Amber', 'Thorn', 'Elder', 'Raven', 'Wild', 'Fox', 'Moss', 'Pine',
  'Marsh', 'Gold', 'Silver', 'Bright', 'Dun', 'Cold', 'Storm', 'Hart', 'Briar', 'Winter',
]

const REALM_B = [
  'dale', 'ford', 'stead', 'brook', 'field', 'haven', 'wick', 'worth', 'bury', 'glen',
  'holm', 'combe', 'mere', 'thorpe', 'moor', 'wood', 'vale', 'march', 'hollow', 'watch',
  'reach', 'crest', 'shire', 'fell', 'gate', 'ridge', 'barrow', 'helm',
]

const MAX_LEN = 24 // matches NAME_MAX

/**
 * A random anonymous realm name. `rand` defaults to Math.random so the client can
 * call it directly; the server passes its world RNG. `taken` (optional) rejects a
 * name already in use so a fresh village doesn't collide with an existing one.
 */
export function randomRealmName(rand: () => number = Math.random, taken?: (n: string) => boolean): string {
  for (let i = 0; i < 20; i++) {
    const n = pick(rand, REALM_A) + pick(rand, REALM_B)
    if (n.length <= MAX_LEN && !(taken && taken(n))) return n
  }
  // Everything short collided — append a number (still within NAME_MAX).
  return (pick(rand, REALM_A) + pick(rand, REALM_B)).slice(0, MAX_LEN - 3) + ' ' + randInt(rand, 2, 99)
}


// ---- name moderation --------------------------------------------------------------
// Normalizes leet-speak and separators, then screens for slurs (substring) and
// impersonation terms (word match). Shared so the client can pre-check and the
// server can enforce.

const LEET: Record<string, string> = {
  '0': 'o', '1': 'i', '2': 'z', '3': 'e', '4': 'a', '5': 's', '6': 'g', '7': 't',
  '8': 'b', '9': 'g', '@': 'a', '$': 's', '!': 'i', '|': 'i', '+': 't',
}

/** Substring-matched after normalization: slurs and hate terms. */
const BANNED_SUBSTRINGS = [
  'nigg', 'niga', 'nigr', 'negro', 'faggot', 'fagot', 'kike', 'spic', 'wetback',
  'chink', 'gook', 'beaner', 'tranny', 'dyke', 'coon', 'raghead', 'towelhead',
  'heil', 'hitler', 'nazi', 'kkk', 'lynch', 'rapist', 'pedo', 'cunt',
]

/** Word-matched after normalization: impersonation / trickery. */
const RESERVED_WORDS = [
  'admin', 'administrator', 'moderator', 'mod', 'system', 'official', 'staff',
  'owner', 'developer', 'dev', 'gm', 'serfland', 'server', 'support',
]

function normalizeName(raw: string): string {
  let s = raw.toLowerCase()
  let out = ''
  for (const ch of s) out += LEET[ch] ?? ch
  return out.replace(/[^a-z]+/g, ' ').trim()
}

/** True when a player-chosen name is acceptable. */
export function isNameAllowed(raw: string): boolean {
  const norm = normalizeName(raw)
  const squashed = norm.replace(/ /g, '')
  for (const bad of BANNED_SUBSTRINGS) {
    if (squashed.includes(bad)) return false
  }
  const words = norm.split(' ')
  for (const w of words) {
    if (RESERVED_WORDS.includes(w)) return false
  }
  return true
}
